import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const issues = [
  '新商品を出したいが、発想や進め方が見えない',
  '既存商品のリニューアルの方向性が決まらない',
  '売れている理由、売れていない理由がわからない',
  '経営・開発・営業の認識がそろわず、議論が進まない',
  '売り方、営業資料、パッケージに一貫性がない',
  'コンセプトはあるが、開発・品質・原価・販路の論点が整理できていない',
];

const menu = [
  {
    number: '01',
    name: '事業戦略・商品開発\nプランニング',
    tag: 'まずはここから',
    when: '新事業・新商品や既存品リニューアルの方向性がわからない、決められないとき。',
    weDo:
      '市場・競合・自社の状況を踏まえ、各部署が一つの方向を向いて進むための戦略企画書を作成し、全体方針と具体的アクションをご提案いたします。',
    goal: '進むべき方向と、次のアクションが決まる',
  },
  {
    number: '02',
    name: '商品化・事業化のプロジェクト伴走',
    when: '構想はあるが、何をすればいいのか、どう進めるべきか、わからないとき。',
    weDo:
      '意思決定のための事業計画書、商品化に向けた関係各所への具体的指示書、その他プロジェクトマネジメント。',
    goal: '商品化が、止まらずに進み続ける',
  },
  {
    number: '03',
    name: '業務の仕組み化',
    when: '業務が属人的で遅く、再現性がないとき。',
    weDo:
      '商品企画、調査、営業資料づくりなど繰り返し発生する重要業務を、AIを含めて見直し、最適な形に組み直します。',
    goal: '社内で回り続ける業務フローが残っている。',
  },
];

const customerVoices = [
  {
    number: '01',
    company: '株式会社エコロギー様',
    project: '新素材プロテインバー',
    title: '独自の新素材を、プロダクトへ落とし込む。',
    body: '私たちの持つ素材を市場にリーチするため、新製品開発を構想した際に、dot Blue様にお手伝いいただきました。最初の製品カテゴリ選定から商品コンセプト、開発ロードマップ、具体的な設計、開発に向けた技術的懸念点、マーケティングメッセージ、販売戦略まで横断的に整理。構想段階のアイデアを実行可能なプロジェクトに落とし込み、丁寧に伴走してもらいました。',
    logoSrc: '/images/food/ecologgie-logo.png',
    logoAlt: '株式会社エコロギー様 ロゴ',
    logoClassName: 'max-h-[6.25rem] max-w-[10.9rem]',
    tags: ['カテゴリ選定', '商品コンセプト', '開発ロードマップ', '販売戦略'],
  },
  {
    number: '02',
    company: '合同会社YOLO様',
    project: 'フィリング入りデーツ',
    title: '海外のデーツを、日本で選ばれる形に。',
    body: 'サウジアラビアで見つけたデーツをテストマーケティングしたところ、反応がとてもよかったのですが、日本で現実的に売るには、製品設計を変えて売り方をフィットさせる必要がありました。当社は食事業の経験がなかったのですが、輸出入サポートから製品コンセプト開発、OEMとのコミュニケーション、販路・デリバリー戦略を一気通貫で設計。机上の空論で終わらせず、実行まで進める道筋を描いてくれました。',
    logoSrc: '/images/food/yolo-logo.png',
    logoAlt: '合同会社YOLO様 ロゴ',
    logoClassName: 'max-h-[4.4rem] max-w-[7.7rem]',
    tags: ['商品コンセプト', '製品設計', 'OEM折衝', '販路・デリバリー戦略'],
  },
];

const flow = [
  {
    number: '01',
    title: '初回相談',
  },
  {
    number: '02',
    title: '取り組みテーマを決める',
  },
  {
    number: '03',
    title: '詳細ヒアリング、現場視察',
  },
  {
    number: '04',
    title: 'アウトプット提出(企画書、調査サマリー等)',
  },
  {
    number: '05',
    title: '必要に応じて拡張',
  },
];

const strengths = [
  {
    title: '食品領域の深い経験知',
    body: '大手食品メーカーでの商品企画・営業・マーケティングの実務。現場の意思決定がどう進むかを知っています。',
  },
  {
    title: '戦略×商品×実行',
    body: '抽象論で終わらせません。戦略を商品に落とし込み、開発・生産・品質・営業・販路まで横断して、実行まで進めます。',
  },
  {
    title: 'AIによるスピード×クオリティ',
    body: '調査・整理・資料化をAIで効率化し、検討の幅と質を落とさずに速く進めます。',
  },
];

const topicOptions = [
  '新商品をつくりたい',
  '既存商品のリニューアル',
  '商品化・事業化を前に進めたい',
  '輸入食品の日本展開',
  '業務の仕組み化・AI活用',
  'その他・まだ決まっていない',
];

function Label({ children }: { children: string }) {
  return (
    <div>
      <span className="mb-4 block h-1 w-8 bg-[#111111]" aria-hidden="true" />
      <p
        className="text-xs font-bold uppercase tracking-[0.16em] text-[#111111]"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        {children}
      </p>
    </div>
  );
}

function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }

    const shouldReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (shouldReduceMotion) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transform transition-all duration-[600ms] ease-out motion-reduce:translate-y-0 motion-reduce:opacity-100 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
      }`}
    >
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="text-sm font-bold text-[#111111]">{children}</span>;
}

export default function FoodPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const siteUrl = 'https://www.aidotblue.com/';
  const canonicalUrl = 'https://www.aidotblue.com/food';
  const seoTitle = '食品事業者のマーケティングパートナー | dot Blue';
  const seoDescription =
    'dot Blueは、食品事業の商品企画・開発を確実に前へ進めるプロジェクトマネジメントとして、商品化・事業化の伴走、新商品開発／既存品改定プランニング、業務の仕組み化を支援します。';
  const ogDescription =
    '食品事業の商品企画・開発を確実に前へ進めるプロジェクトマネジメント。商品化・事業化の伴走、新商品開発／既存品改定プランニング、業務の仕組み化を支援します。';

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'dot Blue',
        url: siteUrl,
      },
      {
        '@type': 'WebPage',
        name: seoTitle,
        url: canonicalUrl,
        description: seoDescription,
        inLanguage: 'ja',
        isPartOf: {
          '@type': 'WebSite',
          name: 'dot Blue',
          url: siteUrl,
        },
        about: {
          '@type': 'Service',
          name: '食品事業者向けマーケティング機能外部化支援',
          serviceType: 'Food marketing strategy and AI utilization support',
          provider: {
            '@type': 'Organization',
            name: 'dot Blue',
            url: siteUrl,
          },
          areaServed: {
            '@type': 'Country',
            name: 'Japan',
          },
        },
      },
    ],
  };

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormStatus('submitting');

    const form = event.currentTarget;
    const formData = new FormData(form);
    const getValue = (key: string) => String(formData.get(key) || '').trim();
    const topic = getValue('topic');
    const rawMessage = getValue('message');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company: getValue('company'),
          department: getValue('department'),
          name: getValue('name'),
          email: getValue('email'),
          message: `【ご相談の種類】${topic}\n\n${rawMessage}`,
          website: getValue('website'),
        }),
      });

      if (!response.ok) {
        throw new Error('Contact request failed');
      }

      form.reset();
      setFormStatus('success');
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords="dot Blue, 食品マーケティング, 食品商品企画, 営業提案, 販路開拓, OEM"
        ogTitle={seoTitle}
        ogDescription={ogDescription}
        canonicalUrl={canonicalUrl}
        ogImage={`${siteUrl}images/food/hero-food-strategy.png`}
        schema={webPageSchema}
      />

      <div className="min-h-screen bg-white text-[#111111]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
        <header className="bg-white">
          <div className="mx-auto flex min-h-[86px] max-w-7xl items-center justify-between gap-3 px-5 py-3 sm:gap-5 sm:px-8 lg:px-10">
            <Link to="/" className="block min-w-0 shrink" aria-label="dot Blue">
              <img
                src="/images/food/dot-blue-logo-wide.png"
                alt="dot Blue"
                className="h-[5.1rem] max-w-[287px] object-contain sm:h-[5.9rem] sm:max-w-none"
              />
            </Link>
            <nav
              className="hidden flex-1 items-center justify-end gap-3 text-[0.68rem] font-bold uppercase tracking-[0.1em] text-[#111111] lg:flex xl:gap-4 xl:text-xs"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <a href="#issues" className="hover:underline">
                Problem
              </a>
              <a href="#services" className="hover:underline">
                Service
              </a>
              <a href="#case-studies" className="hover:underline">
                Case
              </a>
              <a href="#why" className="hover:underline">
                Why
              </a>
              <a href="#process" className="hover:underline">
                Process
              </a>
              <a href="#contact" className="hover:underline">
                Contact
              </a>
            </nav>
            <a
              href="#contact"
              className="shrink-0 bg-[#111111] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#333333] sm:px-5 sm:py-2.5 sm:text-base"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              無料で相談する
            </a>
          </div>
        </header>

        <main>
          <section id="hero" className="relative min-h-[620px] overflow-hidden bg-white sm:min-h-[680px] lg:min-h-[720px]">
            <img
              src="/images/food/hero-food-strategy.png"
              alt="食品素材と調味料が広がるビジュアル"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/40 lg:via-white/85 lg:to-white/10" aria-hidden="true" />
            <div className="relative mx-auto flex min-h-[620px] max-w-7xl items-center px-5 py-16 sm:min-h-[680px] sm:px-8 lg:min-h-[720px] lg:px-10">
              <Reveal className="max-w-2xl">
                <h1
                  className="text-[3.7rem] uppercase leading-[0.86] text-[#111111] sm:text-[5.7rem] lg:text-[7.2rem]"
                  style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                >
                  FOOD STRATEGY,
                  <br />
                  FORWARD.
                </h1>
                <p className="mt-5 max-w-3xl text-[1.2rem] font-bold leading-[1.9] text-[#111111] sm:text-[1.35rem]">
                  <span className="block">食品事業者の事業・商品開発パートナーとして</span>
                  <span className="block">戦略・商品・実行まで一気通貫で支援します</span>
                </p>
                <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center bg-[#111111] px-7 py-4 text-base font-bold text-white transition-colors hover:bg-[#333333]"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    無料で相談する
                  </a>
                  <a
                    href="#services"
                    className="text-sm font-bold underline underline-offset-4 sm:text-base"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    相談メニューを見る ↓
                  </a>
                </div>
              </Reveal>
            </div>
          </section>

          <section id="issues" className="bg-white">
            <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-10">
              <Reveal>
                <Label>Problem</Label>
                <h2 className="mt-4 max-w-5xl text-3xl font-bold leading-[1.3] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-4xl lg:text-[2.75rem]">
                  こんなことで、
                  <br />
                  事業開発・商品開発が止まっていませんか。
                </h2>
              </Reveal>
              <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {issues.map((issue) => (
                  <Reveal key={issue} className="h-full">
                    <div className="h-full min-w-0 border border-[#D9D9D9] p-5 sm:p-6">
                      <span className="mb-5 block h-1 w-8 bg-[#111111]" aria-hidden="true" />
                            <p className="text-[1.2rem] font-bold leading-[1.9] text-[#111111] [text-wrap:balance] [word-break:auto-phrase]">
                              {issue}
                            </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          <section id="services" className="bg-white">
            <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-10">
              <Reveal>
                <Label>Service</Label>
                <h2 className="mt-4 text-3xl font-bold leading-[1.25] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-4xl lg:text-[2.75rem]">
                  dot Blueにできること
                </h2>
                <p className="mt-5 max-w-3xl text-lg font-medium leading-[1.9] text-[#111111]">
                  dot Blueには、次の3つの形でご相談いただけます。
                </p>
              </Reveal>

              <div className="mt-10 border-t border-[#D9D9D9]">
                {menu.map((item) => (
                  <Reveal key={item.number}>
                    <article className="border-b border-[#D9D9D9] py-7 sm:py-8">
                      <div className="grid gap-7 lg:grid-cols-[0.78fr_1.22fr] lg:gap-10">
                        <div className="flex items-start gap-5 sm:gap-6">
                          <span
                            className="flex h-16 w-16 shrink-0 items-center justify-center bg-[#111111] text-4xl text-white"
                            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                          >
                            {item.number}
                          </span>
                          <div className="min-w-0">
                            {item.tag && (
                              <p
                                className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-[#0057D8]"
                                style={{ fontFamily: "'Manrope', sans-serif" }}
                              >
                                {item.tag}
                              </p>
                            )}
                            <h3 className="whitespace-pre-line text-xl font-bold leading-[1.45] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-2xl">
                              {item.name}
                            </h3>
                          </div>
                        </div>
                        <div className="grid gap-5 sm:grid-cols-3">
                          <div>
                            <p
                              className="text-xs font-bold uppercase tracking-[0.14em]"
                              style={{ fontFamily: "'Manrope', sans-serif" }}
                            >
                              こんなときに
                            </p>
                            <p className="mt-2 text-base font-medium leading-[1.9] text-[#111111]">
                              {item.when}
                            </p>
                          </div>
                          <div>
                            <p
                              className="text-xs font-bold uppercase tracking-[0.14em]"
                              style={{ fontFamily: "'Manrope', sans-serif" }}
                            >
                              私たちがすること
                            </p>
                            <p className="mt-2 text-base font-medium leading-[1.9] text-[#111111]">
                              {item.weDo}
                            </p>
                          </div>
                          <div>
                            <p
                              className="text-xs font-bold uppercase tracking-[0.14em]"
                              style={{ fontFamily: "'Manrope', sans-serif" }}
                            >
                              めざすゴール
                            </p>
                            <p className="mt-2 text-base font-medium leading-[1.9] text-[#111111]">
                              {item.goal}
                            </p>
                          </div>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>

              <Reveal>
                <div className="mt-8 flex flex-col gap-5 border-t border-[#D9D9D9] pt-7 sm:flex-row sm:items-center sm:justify-between">
                  <p className="max-w-2xl text-base font-bold leading-[1.8] text-[#111111]">
                    いずれも、進め方と体制はご相談のうえで設計します。どれを選べばいいかわからない場合でもお気軽にご相談ください。
                  </p>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <a
                      href="#contact"
                      className="inline-flex items-center justify-center bg-[#111111] px-7 py-4 text-base font-bold text-white transition-colors hover:bg-[#333333]"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      無料で相談する
                    </a>
                    <a
                      href="#case-studies"
                      className="text-sm font-bold underline underline-offset-4 sm:text-base"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      支援実績を見る ↓
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </section>

          <section id="visual-break" className="bg-white">
            <img
              src="/images/food/food-marketing-flow-band.png"
              alt="食品素材と販路をつなぐ食品マーケティングの流れを表すビジュアル"
              className="h-[210px] w-full object-cover object-center sm:h-[300px] lg:h-[380px]"
              loading="lazy"
            />
          </section>

          <div className="bg-[#F7F7F5]">
            <section id="case-studies" aria-labelledby="customer-voices-heading">
              <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-10">
                <Reveal>
                  <Label>Case Studies</Label>
                  <h2 id="customer-voices-heading" className="mt-4 text-3xl font-bold leading-[1.25] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-4xl lg:text-[2.75rem]">
                    支援実績とお客様の声
                  </h2>
                  <p className="mt-5 max-w-3xl text-lg font-medium leading-[1.9] text-[#111111]">
                    アイデアを、商品になるところまで一緒に進めた例です。
                  </p>
                </Reveal>

                <div className="mt-9 grid items-stretch gap-5 lg:grid-cols-2">
                  {customerVoices.map((voice) => (
                    <Reveal key={voice.number} className="h-full">
                      <article className="flex h-full flex-col border border-[#D9D9D9] bg-white p-5 sm:p-7">
                        <div
                          className={`flex h-36 items-center border border-[#D9D9D9] px-5 py-5 ${
                            voice.logoSrc ? 'gap-4' : 'justify-center text-center'
                          }`}
                        >
                          {voice.logoSrc && (
                            <img
                              src={voice.logoSrc}
                              alt={voice.logoAlt}
                              className={`${voice.logoClassName} shrink-0 object-contain`}
                              loading="lazy"
                            />
                          )}
                          <p className="text-xl font-bold leading-[1.35] text-[#111111] sm:text-2xl">
                            {voice.company}
                          </p>
                        </div>
                        <p
                          className="mt-6 text-sm font-bold uppercase tracking-[0.14em] text-[#111111]"
                          style={{ fontFamily: "'Manrope', sans-serif" }}
                        >
                          CASE {voice.number}
                        </p>
                        <p className="mt-4 min-h-8 text-sm font-bold leading-[1.8] text-[#111111]">{voice.project}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {voice.tags.map((tag) => (
                            <span
                              key={tag}
                              className="border border-[#D9D9D9] px-3 py-1 text-xs font-bold text-[#111111]"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <h3 className="mt-4 min-h-[4.2rem] text-xl font-bold leading-[1.45] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-2xl">
                          {voice.title}
                        </h3>
                        <p className="mt-5 flex-1 text-base font-medium leading-[1.9] text-[#111111]">
                          {voice.body}
                        </p>
                      </article>
                    </Reveal>
                  ))}
                </div>
              </div>
            </section>

            <section id="why">
              <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-10">
                <Reveal>
                  <Label>Why dot Blue</Label>
                  <div className="mt-5 max-w-5xl">
                    <h2 className="text-3xl font-bold leading-[1.25] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-4xl lg:text-[2.75rem]">
                      業界でも、希少な組み合わせ
                    </h2>
                    <p className="mt-5 max-w-4xl text-lg font-medium leading-[1.9] text-[#111111] [text-wrap:balance] [word-break:auto-phrase]">
                      食品の現場とAI実装、その両方を内側から知っている——この組み合わせが、dot Blueの希少性です。
                    </p>
                  </div>
                </Reveal>

                <div className="mt-10 grid border-t border-[#D9D9D9] lg:grid-cols-3 lg:border-l">
                  {strengths.map((strength) => (
                    <Reveal key={strength.title} className="h-full">
                      <article className="h-full border-b border-[#D9D9D9] py-7 lg:border-r lg:px-7">
                        <h3 className="text-xl font-bold leading-[1.45] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-2xl">
                          {strength.title}
                        </h3>
                        <p className="mt-4 text-base font-medium leading-[1.9] text-[#111111]">{strength.body}</p>
                      </article>
                    </Reveal>
                  ))}
                </div>

                <div className="mt-10 grid border-t border-[#D9D9D9] lg:grid-cols-2 lg:border-l">
                  <Reveal className="h-full">
                    <article className="h-full border-b border-[#D9D9D9] py-7 lg:border-r lg:px-7">
                      <p
                        className="text-base font-bold uppercase tracking-[0.14em] text-[#111111] sm:text-lg"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        CEO / Biz
                      </p>
                      <h3 className="mt-3 text-xl font-bold leading-[1.45] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-2xl">
                        平舘 慶大
                      </h3>
                      <p className="mt-5 text-base font-medium leading-[1.9] text-[#111111] sm:text-lg">
                        国際基督教大学卒業後、味の素㈱で13年間、法人営業、商品企画、国内外のマーケティング、海外事業企画などを経験。その他AIやフードテックなど複数スタートアップにも参画。
                      </p>
                    </article>
                  </Reveal>
                  <Reveal className="h-full">
                    <article className="h-full border-b border-[#D9D9D9] py-7 lg:border-r lg:px-7">
                      <p
                        className="text-base font-bold uppercase tracking-[0.14em] text-[#111111] sm:text-lg"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        CTO / Tech
                      </p>
                      <h3 className="mt-3 text-xl font-bold leading-[1.45] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-2xl">
                        藤岡 拓也
                      </h3>
                      <p className="mt-5 text-base font-medium leading-[1.9] text-[#111111] sm:text-lg">
                        九州大学修士課程修了。日立製作所にて、音声AI領域の研究開発と新規事業創出に従事。2023年に独立。AIプロダクトの企画・実装からPMまでを伴走支援。
                      </p>
                    </article>
                  </Reveal>
                </div>
              </div>
            </section>

            <section id="philosophy">
              <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-10">
                <Reveal>
                  <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-14">
                    <div>
                      <Label>Philosophy</Label>
                      <h2 className="mt-4 text-3xl font-bold leading-[1.25] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-4xl lg:text-[2.75rem]">
                        事業開発は、人の意思と実行の積み重ねである
                      </h2>
                    </div>
                    <div className="space-y-6 text-[1.2rem] font-medium leading-[1.95] text-[#111111]">
                      <p>事業開発とは、調達、開発、生産、品質、営業、販路——事業にかかわるすべての工程が複雑に絡みあいます。</p>
                      <p>市場に届ける価値を、自社の「DNA」と「意思」をもって定義し、緻密な計画に落とし込み、泥臭い実行によって、ブランドと商品に落としこまなければなりません。そこまでやって、やっと生活者は喜んでくれます。</p>
                      <p>だから私たちは、お客様が意義の大きい/意味のある商品をつくり、前に進めるためのお手伝いをしています。その先に目指すのは、人々の「おいしい！」をひとつでも増やし、食体験の新発見をしてもらうことです。食品事業者の皆様、ぜひ仕事をご一緒させてください。</p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </section>
          </div>

          <section id="mid-cta" className="bg-white">
            <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:px-10">
              <Reveal>
                <div className="flex flex-col gap-5 border-y border-[#D9D9D9] py-8 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-lg font-bold leading-[1.9] text-[#111111] sm:text-xl">
                    ご相談は無料です。気になっている商品がひとつあれば、それで十分です。
                  </p>
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center bg-[#111111] px-7 py-4 text-base font-bold text-white transition-colors hover:bg-[#333333]"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    無料で相談する
                  </a>
                </div>
              </Reveal>
            </div>
          </section>

          <section id="process" className="bg-white">
            <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-10">
              <Reveal>
                <Label>Process</Label>
                <h2 className="mt-4 text-3xl font-bold leading-[1.25] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-4xl lg:text-[2.75rem]">
                  お取り組みの流れ
                </h2>
                <p className="mt-5 max-w-3xl text-lg font-medium leading-[1.9] text-[#111111]">
                  どの関わり方でも、進み方は同じです。
                </p>
              </Reveal>
              <ol className="mt-10 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
                {flow.map((item) => (
                  <li key={item.number}>
                    <Reveal className="relative border-t border-[#D9D9D9] pt-6">
                      <span
                        className="absolute -top-5 left-0 flex h-10 w-10 items-center justify-center bg-[#111111] text-sm font-bold text-white"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {item.number}
                      </span>
                      <h3 className="text-xl font-bold leading-[1.45] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-2xl">
                        {item.title}
                      </h3>
                    </Reveal>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section id="contact" className="bg-white">
            <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-10">
              <Reveal>
                <div className="border-t border-[#D9D9D9] pt-10">
                  <Label>Small Start</Label>
                  <h2 className="mt-4 text-3xl font-bold leading-[1.3] text-[#111111] [text-wrap:balance] [word-break:auto-phrase] sm:text-4xl lg:text-[2.75rem]">
                    まずは、ひとつのテーマから。
                  </h2>
                  <p className="mt-5 max-w-3xl text-lg font-bold leading-[1.9] text-[#111111]">
                    気になっている商品がひとつあれば、それで十分です。まだ企画になっていない段階のご相談も、相談だけで終わるご相談も歓迎します。食品会社を支援する商社・金融機関の方からのご相談もどうぞ。
                  </p>
                  <form
                    onSubmit={handleContactSubmit}
                    className="mt-8 grid max-w-4xl gap-4"
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="grid gap-2">
                        <FieldLabel>
                          会社名 <span aria-hidden="true">*</span>
                        </FieldLabel>
                        <input
                          name="company"
                          type="text"
                          required
                          className="border border-[#D9D9D9] bg-white px-4 py-3 text-base font-medium text-[#111111] focus:border-[#111111] focus:outline-none"
                        />
                      </label>
                      <label className="grid gap-2">
                        <FieldLabel>
                          名前 <span aria-hidden="true">*</span>
                        </FieldLabel>
                        <input
                          name="name"
                          type="text"
                          required
                          className="border border-[#D9D9D9] bg-white px-4 py-3 text-base font-medium text-[#111111] focus:border-[#111111] focus:outline-none"
                        />
                      </label>
                      <label className="grid gap-2">
                        <FieldLabel>
                          メールアドレス <span aria-hidden="true">*</span>
                        </FieldLabel>
                        <input
                          name="email"
                          type="email"
                          required
                          className="border border-[#D9D9D9] bg-white px-4 py-3 text-base font-medium text-[#111111] focus:border-[#111111] focus:outline-none"
                        />
                      </label>
                      <label className="grid gap-2">
                        <FieldLabel>部署名</FieldLabel>
                        <input
                          name="department"
                          type="text"
                          className="border border-[#D9D9D9] bg-white px-4 py-3 text-base font-medium text-[#111111] focus:border-[#111111] focus:outline-none"
                        />
                      </label>
                      <label className="grid gap-2 sm:col-span-2">
                        <FieldLabel>
                          ご相談の種類 <span aria-hidden="true">*</span>
                        </FieldLabel>
                        <select
                          name="topic"
                          required
                          defaultValue=""
                          className="border border-[#D9D9D9] bg-white px-4 py-3 text-base font-medium text-[#111111] focus:border-[#111111] focus:outline-none"
                        >
                          <option value="" disabled>
                            選択してください
                          </option>
                          {topicOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="hidden" aria-hidden="true">
                      <label>
                        Webサイト
                        <input name="website" type="text" tabIndex={-1} autoComplete="off" />
                      </label>
                    </div>
                    <label className="grid gap-2">
                      <FieldLabel>
                        相談内容 <span className="font-medium">（一言・箇条書きでも構いません）</span> <span aria-hidden="true">*</span>
                      </FieldLabel>
                      <textarea
                        name="message"
                        rows={4}
                        required
                        placeholder="例: 既存商品のリニューアルの方向性を相談したい"
                        className="border border-[#D9D9D9] bg-white px-4 py-3 text-base font-medium text-[#111111] focus:border-[#111111] focus:outline-none"
                      />
                    </label>
                    {formStatus === 'success' && (
                      <p className="border border-[#D9D9D9] bg-white px-4 py-3 text-sm font-bold text-[#111111]">
                        送信が完了しました。
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="border border-[#D9D9D9] bg-white px-4 py-3 text-sm font-bold text-[#111111]">
                        送信に失敗しました。時間をおいて再度お試しください。
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full bg-[#111111] px-8 py-4 text-base font-bold text-white transition-colors hover:bg-[#333333] disabled:cursor-not-allowed disabled:bg-[#333333] sm:w-fit"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {formStatus === 'submitting' ? '送信中...' : '無料で相談する'}
                    </button>
                  </form>
                </div>
              </Reveal>
            </div>
          </section>
        </main>

        <footer className="bg-[#0B0B0C] text-white">
          <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8 lg:px-10">
            <p className="text-sm font-bold text-white">リンク</p>
            <nav className="mt-5 flex flex-wrap gap-x-7 gap-y-3 text-sm font-medium text-[#DDE7F2]">
              <Link to="/privacy-policy" className="hover:underline">
                プライバシーポリシー
              </Link>
              <Link to="/terms" className="hover:underline">
                利用規約
              </Link>
              <Link to="/security" className="hover:underline">
                セキュリティ
              </Link>
              <Link to="/company" className="hover:underline">
                会社概要
              </Link>
            </nav>
            <div className="mt-8 border-t border-white/25 pt-6">
              <p className="text-sm text-[#DDE7F2]">Copyright © dot Blue Inc. All Rights Reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
